import { Color, Spacing } from '@persona-id/design-tokens';
import { lowerCase, upperFirst } from 'lodash/fp';
import { useState } from 'react';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import ChecksTable from 'dashboard/components/ChecksTable';
import Pii from 'dashboard/components/Pii';
import AsyncImage from 'dashboard/components-lib/AsyncImage';
import { formatDate, formatDollar } from 'dashboard/lib/utils/formatters';
import { BankStatementData, DocumentGeneric } from 'dashboard/models/document';
import { VerificationDocument } from 'dashboard/models/verification';
import { DOCUMENT_TYPE_TO_DISPLAY_NAME } from 'dashboard/models/verification-check-config/document-type-detection';
import Attribute, { AttributeGrid } from 'design-system/components/Attribute';
import Card from 'design-system/components/Card';
import TabBar, { Tab } from 'design-system/components/TabBar';
import { Paragraph } from 'design-system/components/Text';

enum DocumentInfoTab {
  Extractions = 'extractions',
  VerificationChecks = 'verification_checks',
}

const FileContainer = styled.div`
  display: grid;
  grid-template-columns: max-content auto;
  grid-column-gap: ${Spacing[5]};
`;

const StyledSection = styled.section`
  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.color.border.primary};
  }
`;

const DualPane = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: ${Spacing[5]};
`;

const StyledAttributeGrid = styled(AttributeGrid)`
  & + & {
    height: fit-content;
    padding-bottom: ${Spacing[1]};
  }
`;

interface BankStatementDocumentInfoProps {
  statement: BankStatementData;
  verification?: VerificationDocument;
  document: DocumentGeneric;
  isRedacted?: boolean;
}

const formatEmptyDate = (date?: string) => {
  if (date || date !== '') {
    return formatDate(date);
  }
  return null;
};

const formatEmptyDollar = (dollar?: number) => {
  if (dollar || dollar === 0) {
    return formatDollar(dollar);
  }
  return null;
};

const BankStatementDocumentInfo = ({
  statement,
  verification,
  document,
  isRedacted = false,
}: BankStatementDocumentInfoProps) => {
  const [selectedTab, setSelectedTab] = useState(DocumentInfoTab.Extractions);

  const documentViewerLink = `/dashboard/document-viewer/${document?.id}`;
  const {
    files,
    'files-normalized': filesNormalized,
    'document-type': classification,
  } = document.attributes;

  // if the document has not been processed it does not have normalized files
  // we default to using normalized files since some image formats that are not compatible
  // with browsers (HEIC images) have been converted to images
  const displayFiles = files.length === filesNormalized.length ? filesNormalized : files;

  return (
    <Card style={{ border: `1px solid ${Color.BorderHairline}` }}>
      <Card.Body>
        <Card.Title>Document info</Card.Title>
        <TabBar>
          <Tab
            id='bank-statement-document-info__tab-extractions'
            selected={selectedTab === DocumentInfoTab.Extractions}
            onClick={() => setSelectedTab(DocumentInfoTab.Extractions)}
          >
            Extractions
          </Tab>
          <Tab
            id='bank-statement-document-info__tab-verification-checks'
            selected={selectedTab === DocumentInfoTab.VerificationChecks}
            onClick={() => setSelectedTab(DocumentInfoTab.VerificationChecks)}
          >
            Verification checks
          </Tab>
        </TabBar>
        {selectedTab === DocumentInfoTab.Extractions && (
          <>
            <FileContainer>
              <Pii isRedacted={false /* AsyncImage has its own empty state */}>
                <Link to={documentViewerLink} state={{ internalRedirect: true }}>
                  <AsyncImage
                    value={displayFiles.map((file) => ({
                      url: file.url,
                      label: file.filename,
                      name: file.filename,
                      type: file['file-type'],
                    }))}
                    showCount
                    showExtension
                    width='120px'
                    height='160px'
                    allowPreviewZoom={false}
                  />
                </Link>
              </Pii>
              <AttributeGrid colCount={2}>
                <Attribute
                  label='File name'
                  value={statement?.['file-info']?.['file-name']}
                  placeholder='No document'
                  redacted={isRedacted}
                />
                <Attribute
                  label='Classification'
                  value={
                    classification != null
                      ? DOCUMENT_TYPE_TO_DISPLAY_NAME[classification]
                        ? DOCUMENT_TYPE_TO_DISPLAY_NAME[classification]
                        : upperFirst(lowerCase(classification))
                      : null
                  }
                  placeholder='Unknown'
                  redacted={isRedacted}
                />
              </AttributeGrid>
            </FileContainer>
            <StyledSection>
              <Paragraph color='primary' weight='bold'>
                Bank details
              </Paragraph>
              <DualPane>
                <StyledAttributeGrid horizontal='full-width'>
                  <Attribute
                    label='Bank name'
                    value={statement?.['statement-info']?.['bank-name']}
                    placeholder='Unknown'
                    redacted={isRedacted}
                  />
                </StyledAttributeGrid>
                <StyledAttributeGrid horizontal='full-width'>
                  <Attribute
                    label='Bank address'
                    value={statement?.['statement-info']?.['bank-address']}
                    placeholder='Unknown'
                    redacted={isRedacted}
                  />
                </StyledAttributeGrid>
              </DualPane>
            </StyledSection>
            <StyledSection>
              <Paragraph color='primary' weight='bold'>
                Account details
              </Paragraph>
              <DualPane>
                <StyledAttributeGrid horizontal='full-width'>
                  <Attribute
                    label='User name'
                    value={statement?.['statement-info']?.['account-holder']}
                    placeholder='Unknown'
                    redacted={isRedacted}
                  />
                  <Attribute
                    label='Account number'
                    value={statement?.['statement-info']?.['account-number']}
                    placeholder='Unknown'
                    redacted={isRedacted}
                  />
                  <Attribute
                    label='Account category'
                    value={statement?.['statement-info']?.['account-category']}
                    placeholder='Unknown'
                    redacted={isRedacted}
                  />
                  <Attribute
                    label='Routing number (ACH)'
                    value={statement?.['statement-info']?.['ach-routing-number']}
                    placeholder='Unknown'
                    redacted={isRedacted}
                  />
                </StyledAttributeGrid>
                <StyledAttributeGrid horizontal='full-width'>
                  <Attribute
                    label='User address'
                    value={statement?.['statement-info']?.['account-holder-address']}
                    placeholder='Unknown'
                    redacted={isRedacted}
                  />
                  <Attribute
                    label='Account type'
                    value={statement?.['statement-info']?.['account-type']}
                    placeholder='Unknown'
                    redacted={isRedacted}
                  />
                  <Attribute
                    label='Routing number (ABA)'
                    value={statement?.['statement-info']?.['aba-routing-number']}
                    placeholder='Unknown'
                    redacted={isRedacted}
                  />
                </StyledAttributeGrid>
              </DualPane>
            </StyledSection>
            <StyledSection>
              <Paragraph color='primary' weight='bold'>
                Statement details
              </Paragraph>
              <DualPane>
                <StyledAttributeGrid horizontal='full-width'>
                  <Attribute
                    label='Beginning balance'
                    value={formatEmptyDollar(statement?.['statement-info']?.['beginning-balance'])}
                    placeholder='Unknown'
                    redacted={isRedacted}
                  />
                  <Attribute
                    label='Ending balance'
                    value={formatEmptyDollar(statement?.['statement-info']?.['ending-balance'])}
                    placeholder='Unknown'
                    redacted={isRedacted}
                  />
                </StyledAttributeGrid>
                <StyledAttributeGrid horizontal='full-width'>
                  <Attribute
                    label='Start date'
                    value={formatEmptyDate(
                      statement?.['statement-info']?.['statement-starting-date'],
                    )}
                    placeholder='Unknown'
                    redacted={isRedacted}
                  />
                  <Attribute
                    label='End date'
                    value={formatEmptyDate(
                      statement?.['statement-info']?.['statement-ending-date'],
                    )}
                    placeholder='Unknown'
                    redacted={isRedacted}
                  />
                </StyledAttributeGrid>
              </DualPane>
            </StyledSection>
          </>
        )}
        {selectedTab === DocumentInfoTab.VerificationChecks && verification?.attributes.checks && (
          <ChecksTable checks={verification?.attributes.checks} />
        )}
      </Card.Body>
    </Card>
  );
};

export default BankStatementDocumentInfo;
